import React from "react";

export interface TabItem<T> {
  k: T;
  label: string;
  icon?: React.ReactNode;
  count?: number | string;
  disabled?: boolean;
}

interface TabSelectorProps<T> {
  tabs: TabItem<T>[];
  active: T;
  onChange: (key: T) => void;
  size?: "md" | "sm";
  fullWidth?: boolean;
  className?: string;
}

const TabSelector = <T extends string>({
  tabs,
  active,
  onChange,
  size = "md",
  fullWidth = false,
  className = "",
}: TabSelectorProps<T>) => {
  return (
    <div
      role="tablist"
      className={`inline-flex items-center h-[var(--tab-container-height)] gap-[var(--tab-container-gap)] p-[var(--tab-container-padding)] bg-[var(--tab-container-bg)] rounded-[var(--tab-container-radius)] max-w-full overflow-x-auto no-scrollbar ${fullWidth ? "w-full" : ""} ${className}`}
    >
      {tabs.map((tab) => {
        const isActive = active === tab.k;
        const isDisabled = Boolean(tab.disabled);

        return (
          <button
            key={tab.k}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-disabled={isDisabled}
            tabIndex={isActive ? 0 : -1}
            disabled={isDisabled}
            onClick={() => {
              if (!isDisabled) onChange(tab.k);
            }}
            className={`inline-flex items-center justify-center gap-1.5 flex-none whitespace-nowrap transition-all ${
              size === "md"
                ? "h-[var(--tab-height-md)] px-[var(--tab-padding-x-md)]"
                : "h-[var(--tab-height-sm)] px-[var(--tab-padding-x-sm)]"
            } rounded-[var(--tab-active-radius)] font-bold ${
              fullWidth ? "flex-1" : ""
            } ${
              isDisabled
                ? "opacity-50 cursor-not-allowed text-[color:var(--tab-inactive-color)] text-[length:var(--tab-inactive-font-size)]"
                : isActive
                  ? "bg-[var(--tab-active-bg)] text-primary-700 shadow-[var(--tab-active-shadow)] border border-[color:rgba(230,227,240,0.6)] text-[length:var(--tab-active-font-size)]"
                  : "text-[color:var(--tab-inactive-color)] text-[length:var(--tab-inactive-font-size)]"
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={`inline-flex items-center justify-center px-[var(--tab-badge-padding-x)] py-[var(--tab-badge-padding-y)] rounded-full text-[length:var(--tab-badge-font-size)] font-bold leading-none ${
                  isActive
                    ? "bg-primary-100 text-primary-700"
                    : "bg-surface-subtle text-[color:var(--tab-badge-color)]"
                }`}
              >
                {tab.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default TabSelector;
